/**
 * ScopeChip — color-coded display of an OAuth scope on an MCP client.
 *
 * Read scopes (`*.read`) → muted; write/admin scopes (`*.write`,
 * `*.admin`, `*.revoke`) → warning; destructive (`*.delete`, `*.purge`)
 * → destructive. Anything else → outlined neutral.
 */
import { cn } from "@/lib/utils";

export interface ScopeChipProps {
  scope: string;
  className?: string;
}

function classify(scope: string): "read" | "write" | "destructive" | "neutral" {
  const s = scope.toLowerCase();
  // Full-admin scopes are the most dangerous → red.
  if (s === "mcp:admin" || /[:.]admin\b/.test(s)) return "destructive";
  if (/\.(delete|purge|destroy)\b/.test(s)) return "destructive";
  if (/[:.](write|revoke|configure|grant)\b/.test(s)) return "write";
  if (/[:.](read|list|get|view|search)\b/.test(s) || s === "mcp:read") return "read";
  return "neutral";
}

const TONE: Record<ReturnType<typeof classify>, string> = {
  read: "bg-muted/40 text-muted-foreground border-border",
  write: "bg-warning-muted text-warning-muted-foreground border-warning-border",
  destructive: "bg-destructive/10 text-destructive border-destructive/30",
  neutral: "bg-card text-foreground border-border",
};

export function ScopeChip({ scope, className }: ScopeChipProps) {
  const tone = classify(scope);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px]",
        TONE[tone],
        className,
      )}
      title={`${tone} scope`}
    >
      {scope}
    </span>
  );
}
